import React, { useState, useEffect } from 'react';

const SecurityTab = () => {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [qrUri, setQrUri] = useState(null);
  const [pairingCode, setPairingCode] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    checkMfaStatus();
  }, []);

  const checkMfaStatus = async () => {
    try {
      const res = await fetch('/api/mfa/status', {
        credentials: 'include',
      });
      const data = await res.json();
      setMfaEnabled(data.enabled);
    } catch (err) {
      console.error('Failed to check MFA status');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupMfa = async () => {
    setSetupLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/mfa/setup', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.qr_uri) {
        setQrUri(data.qr_uri);
        setPairingCode(data.pairing_code);
      } else {
        setError('Failed to generate QR code. Please try again.');
      }
    } catch (err) {
      setError('Failed to set up MFA. Please try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="section profile">
        <h2>Security</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="section profile">
      <h2>Security</h2>

      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: 32,
        marginTop: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: mfaEnabled ? 'linear-gradient(135deg, #00d4aa, #00a88a)' : 'rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <span style={{ fontSize: 24 }}>{mfaEnabled ? '🛡️' : '🔓'}</span>
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 18 }}>SafePulse MFA</h3>
            <p style={{ margin: '4px 0 0', color: '#8b92a8', fontSize: 14 }}>
              {mfaEnabled
                ? 'Two-factor authentication is enabled. You will be asked to approve logins from your phone.'
                : 'Add an extra layer of security by verifying logins with the SafePulse app on your phone.'}
            </p>
          </div>
        </div>

        <div style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          background: mfaEnabled ? 'rgba(0,212,170,0.15)' : 'rgba(255,107,107,0.15)',
          color: mfaEnabled ? '#00d4aa' : '#ff6b6b',
          marginBottom: 20,
        }}>
          {mfaEnabled ? '● Enabled' : '● Not enabled'}
        </div>

        {error && <p style={{ color: '#ff6b6b', marginBottom: 12 }}>{error}</p>}
        {message && <p style={{ color: '#00d4aa', marginBottom: 12 }}>{message}</p>}

        {!mfaEnabled && !qrUri && (
          <div>
            <button
              className="button"
              onClick={handleSetupMfa}
              disabled={setupLoading}
              style={{ maxWidth: 280 }}
            >
              {setupLoading ? 'Generating...' : 'Enable SafePulse MFA'}
            </button>
          </div>
        )}

        {qrUri && (
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12,
            padding: 24,
            marginTop: 16,
            textAlign: 'center',
          }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 16 }}>Scan with SafePulse App</h4>
            <p style={{ color: '#8b92a8', fontSize: 13, marginBottom: 20 }}>
              Open your SafePulse app → tap + → Scan QR Code
            </p>

            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              display: 'inline-block',
              marginBottom: 16,
            }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUri)}`}
                alt="SafePulse QR Code"
                style={{ width: 200, height: 200, display: 'block' }}
              />
            </div>

            <div style={{ marginTop: 12 }}>
              <p style={{ color: '#8b92a8', fontSize: 12, marginBottom: 4 }}>Or enter this code manually:</p>
              <code style={{
                background: 'rgba(0,212,170,0.1)',
                color: '#00d4aa',
                padding: '8px 20px',
                borderRadius: 8,
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: 4,
              }}>
                {pairingCode}
              </code>
            </div>

            <p style={{ color: '#8b92a8', fontSize: 12, marginTop: 16 }}>
              This code expires in 10 minutes.
            </p>

            <button
              className="button"
              onClick={() => { setQrUri(null); setPairingCode(null); checkMfaStatus(); }}
              style={{ marginTop: 16, maxWidth: 200 }}
            >
              Done
            </button>
          </div>
        )}

        {mfaEnabled && (
          <div style={{ marginTop: 8 }}>
            <p style={{ color: '#8b92a8', fontSize: 13 }}>
              To disable MFA, unlink your account from the SafePulse app.
            </p>
          </div>
        )}
      </div>

      <div style={{
        marginTop: 32,
        padding: '20px 24px',
        background: 'rgba(255,255,255,0.02)',
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.05)',
      }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 15 }}>How it works</h4>
        <ol style={{ color: '#8b92a8', fontSize: 13, paddingLeft: 18, lineHeight: 2 }}>
          <li>Download the <strong style={{ color: '#ff3b30' }}>SafePulse</strong> app on your phone</li>
          <li>Click "Enable SafePulse MFA" above and scan the QR code</li>
          <li>When you log in, you'll get a notification on your phone</li>
          <li>Draw the verification shape to approve the login</li>
        </ol>
      </div>
    </div>
  );
};

export default SecurityTab;
